import { useCallback, useState, useMemo, useEffect, type MouseEvent } from 'react';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import WarningIcon from '@mui/icons-material/Warning';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    Tooltip,
    Checkbox,
    Box,
    Typography,
    Button,
    IconButton,
    Menu,
    MenuItem,
    FormControlLabel
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import { ArchiveConfirmationDialog } from './ArchiveConfirmationDialog';
import ClassesContainer from './ClassesContainer';
import ClassFilters from './ClassFilters';
import ClassTableRow from './ClassTableRow';
import { DownloadChatModal } from './DownloadChat/DownloadChatModal';
import { DownloadTranscriptModal } from './DownloadChat/DownloadTranscriptModal';
import LoadingTableRows from './LoadingTableRows';
import {
    type Direction,
    DirectionOption,
    type SortBy,
    SortOption,
    TimeFilter,
    ArchiveOperationType,
    ArchiveAction,
    type ArchiveOperation
} from './types';
import { PaginationContainer, TablePagination, useTablePagination } from '../../../../components/TablePagination';
import { ClassStatus } from '../../../../context/types';
import { Status } from '../../../../types/apiTypes';

const StyledTable = styled(Table)`
    margin-bottom: 0; /* Remove bottom margin to fill container */
    table-layout: fixed;
    width: 100%;
    min-width: 800px; /* Ensure minimum width for all columns */

    /* Prevent content overflow from affecting layout */
    word-break: break-word;
`;

const MainContainer = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 60vh; /* Minimum height to ensure table is substantial */
    width: 100%;
`;

const FiltersAndControlsContainer = styled.div`
    flex-shrink: 0; /* Don't compress the filters and controls */
    background: transparent;
`;

const TableContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 400px; /* Ensure table has substantial height */
    overflow: hidden;

    .table-scroll-wrapper {
        overflow-x: auto;
        overflow-y: auto;
        flex: 1;
        max-height: 70vh; /* Limit maximum height to allow page scrolling */
        min-height: 300px; /* Minimum height for usability */

        /* Custom scrollbar for better UX */
        &::-webkit-scrollbar {
            height: 8px;
            width: 8px;
        }

        &::-webkit-scrollbar-track {
            background: ${(props) => props.theme.colors.gray};
            border-radius: 4px;
        }

        &::-webkit-scrollbar-thumb {
            background: ${(props) => props.theme.colors.border};
            border-radius: 4px;
        }

        &::-webkit-scrollbar-thumb:hover {
            background: ${(props) => props.theme.colors.primary.light};
        }
    }
`;

const StyledTableHead = styled(TableHead)`
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: ${(props) => props.theme.colors.white};
    border-bottom: 2px solid ${(props) => props.theme.colors.border};
`;

const TableWrapper = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0; /* Allow proper shrinking in flex container */
`;

const SelectAllContainer = styled(Box)`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 0;
    padding: 8px 0;
`;

const ColumnMenuContainer = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
`;

const WarningText = styled(Typography)`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const WarningBox = styled(Box)`
    margin-bottom: 16px;
    padding: 8px;
    background-color: ${(props) => props.theme.colors.primary.light};
    border: 1px solid ${(props) => props.theme.colors.primary.main};
    border-radius: 4px;
    font-size: 14px;
`;

const StyledActionsCell = styled(TableCell)`
    width: 80px;
    min-width: 80px;
    max-width: 80px;
`;

const ActionsContainer = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`;

const ActionsLabel = styled(Typography)`
    font-weight: 500;
    font-size: 14px;
    color: ${(props) => props.theme.colors.text.main};
    line-height: 1.5;
`;

const ColumnMenuButton = styled(IconButton)`
    padding: 4px;
    margin-left: 4px;
`;

const StyledSelectCell = styled(TableCell)`
    width: 48px;
    min-width: 48px;
    max-width: 48px;
`;

const StyledNameCell = styled(TableCell)`
    width: 35%;
    min-width: 200px;
`;

const StyledInstructorCell = styled(TableCell)`
    width: 25%;
    min-width: 150px;
`;

const StyledDatesCell = styled(TableCell)`
    width: 20%;
    min-width: 120px;
`;

const StyledCreatedCell = styled(TableCell)`
    width: 15%;
    min-width: 100px;
`;

const StyledPaginationContainer = styled(PaginationContainer)`
    padding-top: 16px;
    flex-shrink: 0;
`;

// Screen reader only styles
const GlobalStyle = `
    .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }
`;

const ClassesTableBody = () => {
    const {
        uuid,
        status,
        sortBy,
        setSortBy,
        sortDirection,
        selectedClass,
        filteredClasses,
        setSortDirection,
        setSelectedClass,
        selectedDownloadTranscriptsClass,
        setSelectedDownloadTranscriptsClass,
        columnVisibility,
        toggleColumnVisibility,
        batchUpdateClassStatus,
        timeFilter
    } = useContainer(ClassesContainer);

    const { t } = useTranslation('admin_settings');

    const { onRowsPerPageChange, paginatedValues, onPageChange, rowsPerPage, setPage, count, page } =
        useTablePagination(filteredClasses, 25);

    const isArchivedFilter = timeFilter === TimeFilter.Archived;

    // State for managing selected rows
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
    const [lastClickedIndex, setLastClickedIndex] = useState<number | null>(null);

    // State for column visibility menu
    const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);

    const [archiveOperation, setArchiveOperation] = useState<ArchiveOperation | null>(null);
    const [isArchiving, setIsArchiving] = useState(false);

    const getBulkActionAriaLabel = useCallback(() => {
        if (selectedRows.size > 0) {
            const actionText = isArchivedFilter ? t('classes.unarchive') : t('classes.archive');
            return `${actionText} ${selectedRows.size} ${t('classes.selected-classes')}`;
        }
        return isArchivedFilter ? t('classes.unarchive-selected') : t('classes.archive-selected');
    }, [selectedRows.size, isArchivedFilter, t]);

    const selectableClasses = paginatedValues.filter((classRecord) => classRecord.status !== ClassStatus.Draft);
    const selectableUuids = selectableClasses.map((classRecord) => classRecord.class_uuid);
    const allSelectableSelected = selectableUuids.length > 0 && selectableUuids.every((uuid) => selectedRows.has(uuid));
    const someSelectableSelected = selectableUuids.some((uuid) => selectedRows.has(uuid));
    const selectAllCheckboxIndeterminate = someSelectableSelected && !allSelectableSelected;
    const hasDraftClasses = paginatedValues.some((c) => c.status === ClassStatus.Draft);

    const isLargeDataset = paginatedValues.length > 250;

    // Reset selections when paginatedValues changes (e.g., when filters change)
    useEffect(() => {
        setSelectedRows(new Set());
        setLastClickedIndex(null);
    }, [paginatedValues]);

    // Clear selections when page changes
    const handlePageChange = useCallback(
        (event: unknown, newPage: number) => {
            setSelectedRows(new Set());
            setLastClickedIndex(null);
            onPageChange(event, newPage);
        },
        [onPageChange]
    );

    const handleRowsPerPageChange = useCallback(
        (value: number) => {
            setSelectedRows(new Set());
            setLastClickedIndex(null);
            onRowsPerPageChange(value);
        },
        [onRowsPerPageChange]
    );

    // Handle individual row selection
    const handleRowSelection = useCallback(
        (classUuid: string, checked: boolean, shiftKey: boolean = false) => {
            const currentIndex = paginatedValues.findIndex((item) => item.class_uuid === classUuid);

            setSelectedRows((prev) => {
                const newSet = new Set(prev);

                if (shiftKey && lastClickedIndex !== null && currentIndex !== -1) {
                    // Range selection with shift+click, excluding draft classes
                    const startIndex = Math.min(lastClickedIndex, currentIndex);
                    const endIndex = Math.max(lastClickedIndex, currentIndex);

                    for (let i = startIndex; i <= endIndex; i++) {
                        // Skip draft classes in range selection
                        if (paginatedValues[i].status === ClassStatus.Draft) {
                            continue;
                        }

                        if (checked) {
                            newSet.add(paginatedValues[i].class_uuid);
                        } else {
                            newSet.delete(paginatedValues[i].class_uuid);
                        }
                    }
                } else {
                    // Normal single selection - allow draft classes to be selected/deselected individually
                    if (checked) {
                        newSet.add(classUuid);
                    } else {
                        newSet.delete(classUuid);
                    }
                }

                return newSet;
            });

            // Update last clicked index for range selection
            setLastClickedIndex(currentIndex);
        },
        [paginatedValues, lastClickedIndex]
    );

    // Handle select all toggle
    const handleSelectAll = useCallback(
        (checked: boolean) => {
            setLastClickedIndex(null);
            if (checked) {
                // Select all visible rows, excluding draft classes
                const selectableUuids = paginatedValues
                    .filter((classRecord) => classRecord.status !== ClassStatus.Draft)
                    .map((classRecord) => classRecord.class_uuid);
                setSelectedRows(new Set(selectableUuids));
            } else {
                // Deselect all rows
                setSelectedRows(new Set());
            }
        },
        [paginatedValues]
    );

    // Handle archive selected classes
    const handleArchiveSelected = useCallback(() => {
        if (selectedRows.size === 0) return;
        setArchiveOperation({
            type: ArchiveOperationType.Batch,
            classUuids: Array.from(selectedRows),
            action: ArchiveAction.Archive
        });
    }, [selectedRows]);

    // Handle unarchive selected classes
    const handleUnarchiveSelected = useCallback(() => {
        if (selectedRows.size === 0) return;
        setArchiveOperation({
            type: ArchiveOperationType.Batch,
            classUuids: Array.from(selectedRows),
            action: ArchiveAction.Unarchive
        });
    }, [selectedRows]);

    // Handle confirmed archive/unarchive operation
    const handleConfirmArchive = useCallback(async () => {
        if (!archiveOperation) return;

        try {
            setIsArchiving(true);

            // Use the action directly from the operation
            await batchUpdateClassStatus(archiveOperation.classUuids, archiveOperation.action);

            // After successful operation, clear selections for batch operations
            if (archiveOperation.type === ArchiveOperationType.Batch) {
                setSelectedRows(new Set());
            }
        } catch (error) {
            console.error('Error updating class status:', error);
        } finally {
            setArchiveOperation(null);
            setIsArchiving(false);
        }
    }, [archiveOperation, batchUpdateClassStatus]);

    // Handle archive/unarchive single class
    const handleArchiveSingle = useCallback(
        (classUuid: string, currentStatus: string) => {
            // Find the class to get its name
            const classRecord = filteredClasses.find((c) => c.class_uuid === classUuid);
            if (!classRecord) return;

            // Set the archive operation data
            setArchiveOperation({
                type: ArchiveOperationType.Single,
                classUuids: [classUuid],
                className: classRecord.class_name,
                currentStatus,
                action: currentStatus === ClassStatus.Archived ? ArchiveAction.Unarchive : ArchiveAction.Archive
            });
        },
        [filteredClasses]
    );

    // Handle column visibility menu
    const handleColumnMenuClick = (event: MouseEvent<HTMLElement>) => {
        setColumnMenuAnchor(event.currentTarget);
    };

    const handleColumnMenuClose = () => {
        setColumnMenuAnchor(null);
    };

    const handleColumnToggle = (column: keyof typeof columnVisibility) => {
        toggleColumnVisibility(column);
    };

    const handleSortSelection = useCallback(
        ({ direction, _sortBy }: { direction: Direction; _sortBy: SortBy }) => {
            if (sortBy !== _sortBy) {
                setSortDirection(DirectionOption.ASCENDING);
                setSortBy(_sortBy);
                return;
            }

            setSortBy(_sortBy);

            if (!direction) setSortDirection(DirectionOption.ASCENDING);
            if (direction === DirectionOption.ASCENDING) setSortDirection(DirectionOption.DESCENDING);
            if (direction === DirectionOption.DESCENDING) setSortDirection(undefined);
        },
        [setSortBy, setSortDirection, sortBy]
    );

    // Memoize table rows to prevent unnecessary re-renders with large datasets
    const tableRows = useMemo(
        () =>
            paginatedValues.map((classRecord, index) => (
                <ClassTableRow
                    key={classRecord.class_uuid}
                    record={classRecord}
                    index={index}
                    isSelected={selectedRows.has(classRecord.class_uuid)}
                    onSelectionChange={handleRowSelection}
                    hasAnySelected={selectedRows.size > 0}
                    onArchive={handleArchiveSingle}
                    columnVisibility={columnVisibility}
                />
            )),
        [paginatedValues, selectedRows, handleRowSelection, handleArchiveSingle, columnVisibility]
    );

    return (
        <MainContainer>
            <style>{GlobalStyle}</style>
            <FiltersAndControlsContainer>
                <ClassFilters setPage={setPage} />

                {isLargeDataset && (
                    <WarningBox role="alert" aria-live="polite">
                        <WarningText variant="body2" color="text.secondary">
                            <WarningIcon fontSize="small" color="warning" />
                            {t('classes.performance-warning', { count: paginatedValues.length })}
                        </WarningText>
                    </WarningBox>
                )}

                <ColumnMenuContainer
                    role="toolbar"
                    aria-label={t('classes.bulk-actions-toolbar')}
                    data-testid="bulk-actions-toolbar"
                >
                    <SelectAllContainer>
                        <Checkbox
                            checked={allSelectableSelected}
                            indeterminate={selectAllCheckboxIndeterminate}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            size="small"
                            aria-label={
                                allSelectableSelected
                                    ? t('classes.deselect-all-classes')
                                    : hasDraftClasses
                                      ? t('classes.select-all-excludes-draft')
                                      : t('classes.select-all-classes')
                            }
                            aria-describedby="selection-status"
                            data-testid="select-all-checkbox"
                        />
                        <Typography variant="body2" id="selection-status">
                            {allSelectableSelected
                                ? t('classes.deselect-all')
                                : hasDraftClasses
                                  ? t('classes.select-all-excludes-draft')
                                  : t('classes.select-all')}
                            {selectedRows.size > 0 && ` (${selectedRows.size} selected)`}
                        </Typography>
                        <Tooltip
                            title={timeFilter === TimeFilter.Draft ? t('classes.draft-cannot-be-archived') : ''}
                            arrow
                        >
                            <span>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={isArchivedFilter ? handleUnarchiveSelected : handleArchiveSelected}
                                    disabled={selectedRows.size === 0 || timeFilter === TimeFilter.Draft}
                                    aria-label={getBulkActionAriaLabel()}
                                    aria-describedby={timeFilter === TimeFilter.Draft ? 'draft-tooltip' : undefined}
                                    data-testid="bulk-action-button"
                                >
                                    {isArchivedFilter ? t('classes.unarchive-selected') : t('classes.archive-selected')}
                                </Button>
                                {timeFilter === TimeFilter.Draft && (
                                    <span id="draft-tooltip" className="sr-only">
                                        {t('classes.draft-cannot-be-archived')}
                                    </span>
                                )}
                            </span>
                        </Tooltip>
                    </SelectAllContainer>
                </ColumnMenuContainer>
            </FiltersAndControlsContainer>

            <TableContainer>
                <TableWrapper>
                    <div className="table-scroll-wrapper">
                        <StyledTable
                            data-testid="classes-table"
                            role="grid"
                            aria-label={t('classes.classes-table')}
                            aria-describedby="table-description"
                            aria-rowcount={filteredClasses.length}
                        >
                            <caption id="table-description" className="sr-only">
                                {t('classes.table-description', {
                                    count: filteredClasses.length,
                                    filter: timeFilter
                                })}
                            </caption>
                            <StyledTableHead>
                                <TableRow>
                                    <StyledSelectCell data-testid="header-checkbox-cell">
                                        <span className="sr-only">{t('classes.select-column-header')}</span>
                                    </StyledSelectCell>
                                    {columnVisibility.className && (
                                        <StyledNameCell data-testid="header-class-name-cell">
                                            <Tooltip arrow title={t('classes.sort')}>
                                                <TableSortLabel
                                                    active={sortBy === SortOption.NAME && !!sortDirection}
                                                    direction={sortDirection}
                                                    onClick={() =>
                                                        handleSortSelection({
                                                            direction: sortDirection,
                                                            _sortBy: SortOption.NAME
                                                        })
                                                    }
                                                >
                                                    {t('classes.name-with-count', {
                                                        classCount: filteredClasses.length
                                                    })}
                                                </TableSortLabel>
                                            </Tooltip>
                                        </StyledNameCell>
                                    )}
                                    {columnVisibility.instructor && (
                                        <StyledInstructorCell data-testid="header-instructor-cell">
                                            <Tooltip arrow title={t('classes.sort')}>
                                                <TableSortLabel
                                                    active={sortBy === SortOption.INSTRUCTOR && !!sortDirection}
                                                    direction={sortDirection}
                                                    onClick={() =>
                                                        handleSortSelection({
                                                            direction: sortDirection,
                                                            _sortBy: SortOption.INSTRUCTOR
                                                        })
                                                    }
                                                >
                                                    {t('classes.instructor')}
                                                </TableSortLabel>
                                            </Tooltip>
                                        </StyledInstructorCell>
                                    )}
                                    {columnVisibility.dates && (
                                        <StyledDatesCell data-testid="header-dates-cell">
                                            <Tooltip arrow title={t('classes.sort')}>
                                                <TableSortLabel
                                                    active={sortBy === SortOption.DATES && !!sortDirection}
                                                    direction={sortDirection}
                                                    onClick={() =>
                                                        handleSortSelection({
                                                            direction: sortDirection,
                                                            _sortBy: SortOption.DATES
                                                        })
                                                    }
                                                >
                                                    {t('classes.dates')}
                                                </TableSortLabel>
                                            </Tooltip>
                                        </StyledDatesCell>
                                    )}
                                    {columnVisibility.created && (
                                        <StyledCreatedCell data-testid="header-created-cell">
                                            <Tooltip arrow title={t('classes.sort')}>
                                                <TableSortLabel
                                                    active={sortBy === SortOption.CREATED && !!sortDirection}
                                                    direction={sortDirection}
                                                    onClick={() =>
                                                        handleSortSelection({
                                                            direction: sortDirection,
                                                            _sortBy: SortOption.CREATED
                                                        })
                                                    }
                                                >
                                                    {t('classes.created')}
                                                </TableSortLabel>
                                            </Tooltip>
                                        </StyledCreatedCell>
                                    )}
                                    <StyledActionsCell data-testid="header-actions-cell">
                                        <ActionsContainer>
                                            <ActionsLabel variant="body2">{t('classes.actions')}</ActionsLabel>
                                            <Tooltip title={t('classes.column-options')}>
                                                <ColumnMenuButton
                                                    onClick={handleColumnMenuClick}
                                                    size="small"
                                                    aria-label={t('classes.column-options')}
                                                    aria-expanded={Boolean(columnMenuAnchor)}
                                                    aria-haspopup="menu"
                                                    data-testid="column-options-button"
                                                >
                                                    <MoreVertIcon fontSize="small" />
                                                </ColumnMenuButton>
                                            </Tooltip>
                                        </ActionsContainer>
                                    </StyledActionsCell>
                                </TableRow>
                            </StyledTableHead>
                            <TableBody>
                                {status === Status.InProgress ? (
                                    <LoadingTableRows columnVisibility={columnVisibility} />
                                ) : null}
                                {status === Status.InProgress && (
                                    <tr>
                                        <td
                                            colSpan={Object.values(columnVisibility).filter(Boolean).length + 2}
                                            className="sr-only"
                                        >
                                            {t('classes.loading-classes')}
                                        </td>
                                    </tr>
                                )}
                                {tableRows}
                            </TableBody>
                        </StyledTable>
                    </div>
                    <StyledPaginationContainer>
                        <TablePagination
                            count={count}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handlePageChange}
                            onRowsPerPageChange={handleRowsPerPageChange}
                        />
                    </StyledPaginationContainer>
                </TableWrapper>
            </TableContainer>
            <Menu
                anchorEl={columnMenuAnchor}
                open={Boolean(columnMenuAnchor)}
                onClose={handleColumnMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                role="menu"
                aria-label={t('classes.column-visibility-menu')}
                data-testid="column-visibility-menu"
            >
                <MenuItem
                    role="menuitemcheckbox"
                    aria-checked={columnVisibility.className}
                    data-testid="toggle-className-column"
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={columnVisibility.className}
                                onChange={() => handleColumnToggle('className')}
                                size="small"
                                aria-label={t('classes.toggle-name-column')}
                            />
                        }
                        label={t('classes.name')}
                    />
                </MenuItem>
                <MenuItem
                    role="menuitemcheckbox"
                    aria-checked={columnVisibility.instructor}
                    data-testid="toggle-instructor-column"
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={columnVisibility.instructor}
                                onChange={() => handleColumnToggle('instructor')}
                                size="small"
                                aria-label={t('classes.toggle-instructor-column')}
                            />
                        }
                        label={t('classes.instructor')}
                    />
                </MenuItem>
                <MenuItem
                    role="menuitemcheckbox"
                    aria-checked={columnVisibility.dates}
                    data-testid="toggle-dates-column"
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={columnVisibility.dates}
                                onChange={() => handleColumnToggle('dates')}
                                size="small"
                                aria-label={t('classes.toggle-dates-column')}
                            />
                        }
                        label={t('classes.dates')}
                    />
                </MenuItem>
                <MenuItem
                    role="menuitemcheckbox"
                    aria-checked={columnVisibility.created}
                    data-testid="toggle-created-column"
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={columnVisibility.created}
                                onChange={() => handleColumnToggle('created')}
                                size="small"
                                aria-label={t('classes.toggle-created-column')}
                            />
                        }
                        label={t('classes.created')}
                    />
                </MenuItem>
            </Menu>
            <DownloadChatModal
                open={Boolean(selectedClass)}
                onClose={() => setSelectedClass(null)}
                classId={selectedClass?.class_id}
                className={selectedClass?.class_name}
                userUuid={uuid}
            />
            <DownloadTranscriptModal
                open={Boolean(selectedDownloadTranscriptsClass)}
                onClose={() => setSelectedDownloadTranscriptsClass(null)}
                classId={selectedDownloadTranscriptsClass?.class_id}
                className={selectedDownloadTranscriptsClass?.class_name}
                userUuid={uuid}
                classUuid={selectedDownloadTranscriptsClass?.class_uuid}
            />
            <ArchiveConfirmationDialog
                open={Boolean(archiveOperation)}
                onClose={() => setArchiveOperation(null)}
                onConfirm={handleConfirmArchive}
                type={archiveOperation?.type || ArchiveOperationType.Batch}
                classCount={
                    archiveOperation?.type === ArchiveOperationType.Batch
                        ? archiveOperation.classUuids.length
                        : undefined
                }
                className={
                    archiveOperation?.type === ArchiveOperationType.Single ? archiveOperation.className : undefined
                }
                confirming={isArchiving}
                action={archiveOperation?.action || ArchiveAction.Archive}
            />
        </MainContainer>
    );
};

export default ClassesTableBody;
